<template>
  <div class="overview-dashboard">
    <div v-if="loading" class="loading-state">
      <p>Loading overview data...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button class="retry-btn" @click="loadData">Retry</button>
    </div>

    <div v-else class="overview-content">
      <!-- Summary Stats -->
      <StatsGrid>
        <UserBreakdownCard title="Users" :segments="userBreakdownSegments" />
        <StatsCard
          title="Lists"
          :value="`${overviewData.totalLists} / ${overviewData.deletedLists}`"
          detail="Total / Deleted"
        />
        <StatsCard
          title="PtG Battlepack Lists"
          :value="`${overviewData.totalPathToGloryBattlepackLists} / ${overviewData.pathToGloryBattlepackListsLast30Days}`"
          detail="Total / Last 30 Days"
        />
        <StatsCard
          title="Collection Lists"
          :value="`${overviewData.totalCollectionLists} / ${overviewData.collectionListsLast30Days}`"
          detail="Total / Last 30 Days"
        />
        <StatsCard
          title="Games"
          :value="`${overviewData.totalGames} / ${overviewData.deletedGames}`"
          detail="Total / Deleted"
        />
        <StatsCard title="Event Logs" :value="overviewData.totalEventLogs" />
      </StatsGrid>

      <!-- Charts Section -->
      <div class="charts-section">
        <div class="charts-grid">
          <BarChart
            title="Lists Created (Last 30 Days)"
            :data="overviewData.listCountsByDay"
            :start-date="thirtyDaysAgo"
            :end-date="today"
            color="#10b981"
          />

          <BarChart
            title="Games Created (Last 30 Days)"
            :data="overviewData.gameCountsByDay"
            :start-date="thirtyDaysAgo"
            :end-date="today"
            color="#3b82f6"
          />

          <BarChart
            title="Event Logs (Last 30 Days)"
            :data="overviewData.eventLogCountsByDay"
            :start-date="thirtyDaysAgo"
            :end-date="today"
            color="#f97316"
          />
        </div>
      </div>

      <!-- Client App Versions -->
      <div class="charts-section">
        <div class="charts-grid">
          <CategoryBarChart
            title="Users by App Version"
            :subtitle="`${totalVersionedUsers} tracked`"
            :data="clientVersionRows"
            color="#8b5cf6"
            secondary-color="#3b82f6"
            primary-label="Accounts"
            secondary-label="Anonymous"
          />
        </div>
      </div>

      <!-- Email Delivery (ZeptoMail) -->
      <div class="charts-section">
        <h3 class="section-heading">Email Delivery (Last 30 Days)</h3>

        <div v-if="!emailStatsConfigured" class="empty-state">
          Email reporting is not configured on the server.
        </div>

        <template v-else>
          <StatsGrid>
            <StatsCard title="Sent" :value="emailStats?.totals.sent ?? 0" />
            <StatsCard title="Delivered" :value="emailStats?.totals.delivered ?? 0" />
            <StatsCard title="Hard Bounces" :value="emailStats?.totals.hardBounce ?? 0" />
            <StatsCard title="Soft Bounces" :value="emailStats?.totals.softBounce ?? 0" />
          </StatsGrid>

          <div class="charts-grid">
            <BarChart
              title="Sent (Last 30 Days)"
              :data="emailSentByDay"
              :start-date="thirtyDaysAgo"
              :end-date="today"
              color="#3b82f6"
            />
            <BarChart
              title="Delivered (Last 30 Days)"
              :data="emailDeliveredByDay"
              :start-date="thirtyDaysAgo"
              :end-date="today"
              color="#10b981"
            />
            <BarChart
              title="Bounced (Last 30 Days)"
              :data="emailBouncedByDay"
              :start-date="thirtyDaysAgo"
              :end-date="today"
              color="#ef4444"
            />
          </div>
        </template>
      </div>

      <!-- Activity Summary -->
      <div class="activity-summary">
        <h3>Activity Summary</h3>
        <div class="summary-grid">
          <div class="summary-item">
            <span class="summary-label">Total Lists Created (30 days):</span>
            <div class="summary-value-group">
              <span class="summary-value">{{ totalListsCreatedLast30 }}</span>
              <span :class="['summary-delta', getDeltaClass(totalListsDelta30)]">
                {{ getDeltaArrow(totalListsDelta30) }} {{ formatDelta(totalListsDelta30, 0) }}
                vs previous 30 days
              </span>
            </div>
          </div>
          <div class="summary-item">
            <span class="summary-label">Total Games Created (30 days):</span>
            <div class="summary-value-group">
              <span class="summary-value">{{ totalGamesCreatedLast30 }}</span>
              <span :class="['summary-delta', getDeltaClass(totalGamesDelta30)]">
                {{ getDeltaArrow(totalGamesDelta30) }} {{ formatDelta(totalGamesDelta30, 0) }}
                vs previous 30 days
              </span>
            </div>
          </div>
          <div class="summary-item">
            <span class="summary-label">Average Lists per Day (30 days):</span>
            <div class="summary-value-group">
              <span class="summary-value">{{ avgListsPerDayLast30 }}</span>
              <span :class="['summary-delta', getDeltaClass(avgListsDelta30)]">
                {{ getDeltaArrow(avgListsDelta30) }} {{ formatDelta(avgListsDelta30, 1) }}
                vs previous 30 days
              </span>
            </div>
          </div>
          <div class="summary-item">
            <span class="summary-label">Average Games per Day (30 days):</span>
            <div class="summary-value-group">
              <span class="summary-value">{{ avgGamesPerDayLast30 }}</span>
              <span :class="['summary-delta', getDeltaClass(avgGamesDelta30)]">
                {{ getDeltaArrow(avgGamesDelta30) }} {{ formatDelta(avgGamesDelta30, 1) }}
                vs previous 30 days
              </span>
            </div>
          </div>
          <div class="summary-item">
            <span class="summary-label">Total Event Logs (30 days):</span>
            <div class="summary-value-group">
              <span class="summary-value">{{ totalEventLogsLast30 }}</span>
              <span :class="['summary-delta', getDeltaClass(totalEventLogsDelta30)]">
                {{ getDeltaArrow(totalEventLogsDelta30) }}
                {{ formatDelta(totalEventLogsDelta30, 0) }}
                vs previous 30 days
              </span>
            </div>
          </div>
          <div class="summary-item">
            <span class="summary-label">Average Event Logs per Day (30 days):</span>
            <div class="summary-value-group">
              <span class="summary-value">{{ avgEventLogsPerDayLast30 }}</span>
              <span :class="['summary-delta', getDeltaClass(avgEventLogsDelta30)]">
                {{ getDeltaArrow(avgEventLogsDelta30) }} {{ formatDelta(avgEventLogsDelta30, 1) }}
                vs previous 30 days
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="top-army-lists">
        <h3>Top Event Actions (Last 30 Days)</h3>

        <div v-if="overviewData.topEventActionsLast30Days.length === 0" class="empty-state">
          No event log activity in the last 30 days.
        </div>

        <ol v-else class="top-army-list">
          <li v-for="(entry, index) in overviewData.topEventActionsLast30Days" :key="entry.action">
            <span class="rank">{{ index + 1 }}.</span>
            <span class="faction">{{ entry.action }}</span>
            <span class="count">{{ entry.count }}</span>
          </li>
        </ol>
      </div>

      <div class="top-army-lists">
        <h3>Top Path to Glory Battlepacks (Last 30 Days)</h3>

        <div v-if="overviewData.topPathToGloryBattlepacks.length === 0" class="empty-state">
          No Path to Glory battlepack list activity in the last 30 days.
        </div>

        <ol v-else class="top-army-list">
          <li
            v-for="(entry, index) in overviewData.topPathToGloryBattlepacks"
            :key="entry.battlePack"
          >
            <span class="rank">{{ index + 1 }}.</span>
            <span class="faction">{{ entry.battlePack }}</span>
            <span class="count">{{ entry.count }}</span>
          </li>
        </ol>
      </div>

      <!-- Faction Usage -->
      <div class="top-army-lists">
        <h3>Factions Used (Last 30 Days)</h3>

        <div v-if="factionUsageLast30Days.length === 0" class="empty-state">
          No list activity in the last 30 days.
        </div>

        <ol v-else class="top-army-list">
          <li v-for="(entry, index) in factionUsageLast30DaysWithDelta" :key="entry.faction">
            <span class="rank">{{ index + 1 }}.</span>
            <span class="faction">{{ entry.faction }}</span>
            <span class="count-with-delta">
              <span class="count">{{ entry.count }}</span>
              <span :class="['summary-delta', getDeltaClass(entry.delta)]">
                {{ getDeltaArrow(entry.delta) }} {{ formatDelta(entry.delta, 0) }}
              </span>
            </span>
          </li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import {
  client,
  parseErrorMessage,
  type AdminOverview,
  type EmailStats,
  type ClientVersionCount,
} from '../client.js';
import StatsGrid from './StatsGrid.vue';
import StatsCard from './StatsCard.vue';
import UserBreakdownCard from './UserBreakdownCard.vue';
import BarChart from './BarChart.vue';
import CategoryBarChart from './CategoryBarChart.vue';

const loading = ref(true);
const error = ref<string | null>(null);
const overviewData = ref<AdminOverview>({
  totalUsers: 0,
  premiumUsers: 0,
  giftedPremiumUsers: 0,
  totalAnonUsers: 0,
  totalLists: 0,
  deletedLists: 0,
  totalCollectionLists: 0,
  collectionListsLast30Days: 0,
  totalPathToGloryBattlepackLists: 0,
  pathToGloryBattlepackListsLast30Days: 0,
  totalGames: 0,
  deletedGames: 0,
  totalEventLogs: 0,
  listCountsByDay: [],
  gameCountsByDay: [],
  eventLogCountsByDay: [],
  previousListCountsByDay: [],
  previousGameCountsByDay: [],
  previousEventLogCountsByDay: [],
  factionUsageLast30Days: [],
  factionUsagePrevious30Days: [],
  topEventActionsLast30Days: [],
  topPathToGloryBattlepacks: [],
});

// Date calculations
const today = new Date();
const thirtyDaysAgo = computed(() => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date;
});

// Calculate totals and averages
const sumCounts = (counts: { count: number }[]) =>
  counts.reduce((sum, item) => sum + item.count, 0);

const formatDelta = (delta: number, decimals: number) => {
  const rounded = delta.toFixed(decimals);
  const sign = delta > 0 ? '+' : '';
  return `${sign}${rounded}`;
};

const getDeltaArrow = (delta: number) => {
  if (delta > 0) return '↑';
  if (delta < 0) return '↓';
  return '→';
};

const getDeltaClass = (delta: number) => {
  if (delta > 0) return 'delta-up';
  if (delta < 0) return 'delta-down';
  return 'delta-neutral';
};

// Accounts, anonymous (no-account) devices, and premium — squished into one
// widget instead of separate cards so the three user-population numbers read
// together at a glance.
const userBreakdownSegments = computed(() => [
  { label: 'Accounts', value: overviewData.value.totalUsers },
  { label: 'Anonymous', value: overviewData.value.totalAnonUsers },
  {
    label: 'Premium',
    value: `${overviewData.value.premiumUsers} / ${overviewData.value.giftedPremiumUsers}`,
    detail: 'Paid / Gifted',
  },
]);

const totalListsCreatedLast30 = computed(() => {
  return overviewData.value.listCountsByDay.reduce((sum, item) => sum + item.count, 0);
});

const totalGamesCreatedLast30 = computed(() => {
  return overviewData.value.gameCountsByDay.reduce((sum, item) => sum + item.count, 0);
});

const totalListsCreatedPrevious30 = computed(() => {
  return sumCounts(overviewData.value.previousListCountsByDay);
});

const totalGamesCreatedPrevious30 = computed(() => {
  return sumCounts(overviewData.value.previousGameCountsByDay);
});

const totalEventLogsLast30 = computed(() => {
  return sumCounts(overviewData.value.eventLogCountsByDay);
});

const totalEventLogsPrevious30 = computed(() => {
  return sumCounts(overviewData.value.previousEventLogCountsByDay);
});

const avgListsPerDayLast30 = computed(() => {
  const total = totalListsCreatedLast30.value;
  return total > 0 ? (total / 30).toFixed(1) : '0';
});

const avgGamesPerDayLast30 = computed(() => {
  const total = totalGamesCreatedLast30.value;
  return total > 0 ? (total / 30).toFixed(1) : '0';
});

const avgEventLogsPerDayLast30 = computed(() => {
  const total = totalEventLogsLast30.value;
  return total > 0 ? (total / 30).toFixed(1) : '0';
});

const totalListsDelta30 = computed(
  () => totalListsCreatedLast30.value - totalListsCreatedPrevious30.value,
);
const totalGamesDelta30 = computed(
  () => totalGamesCreatedLast30.value - totalGamesCreatedPrevious30.value,
);
const totalEventLogsDelta30 = computed(
  () => totalEventLogsLast30.value - totalEventLogsPrevious30.value,
);

const factionUsageLast30Days = computed(() => {
  const data: Record<string, number> = {};
  [...overviewData.value.factionUsageLast30Days].forEach((e) => {
    const baseName = e.faction.split(' - ')[0];
    data[baseName] = (data[baseName] ?? 0) + e.count;
  });
  return Object.getOwnPropertyNames(data)
    .map((name) => {
      return { faction: name, count: data[name] };
    })
    .sort((left, right) => {
      if (right.count !== left.count) return right.count - left.count;
      return left.faction.localeCompare(right.faction);
    });
});

const factionUsagePrevious30Days = computed(() => {
  const data: Record<string, number> = {};
  [...overviewData.value.factionUsagePrevious30Days].forEach((e) => {
    const baseName = e.faction.split(' - ')[0];
    data[baseName] = (data[baseName] ?? 0) + e.count;
  });
  return data;
});

const factionUsageLast30DaysWithDelta = computed(() => {
  return factionUsageLast30Days.value.map((entry) => {
    const previous = factionUsagePrevious30Days.value[entry.faction] ?? 0;
    return {
      ...entry,
      delta: entry.count - previous,
    };
  });
});

const avgListsDelta30 = computed(() => totalListsDelta30.value / 30);
const avgGamesDelta30 = computed(() => totalGamesDelta30.value / 30);
const avgEventLogsDelta30 = computed(() => totalEventLogsDelta30.value / 30);

// Client app versions — distinct users by last-seen X-App-Version header.
const clientVersions = ref<ClientVersionCount[]>([]);

const clientVersionRows = computed(() =>
  clientVersions.value.map((entry) => ({
    label:
      entry.version === null
        ? 'Never synced'
        : entry.version === 'unversioned'
          ? 'Pre-23.x'
          : entry.version,
    count: entry.accountUsers,
    secondaryCount: entry.anonUsers,
  })),
);

const totalVersionedUsers = computed(() =>
  clientVersions.value.reduce((sum, entry) => sum + entry.accountUsers + entry.anonUsers, 0),
);

// Email delivery stats (ZeptoMail). null = not configured on the server.
const emailStats = ref<EmailStats | null>(null);
const emailStatsConfigured = ref(true);

const toChartData = (key: 'sent' | 'delivered' | 'bounced') =>
  (emailStats.value?.byDay ?? []).map((d) => ({
    date: new Date(d.date).getTime(),
    count: key === 'bounced' ? d.hardBounce + d.softBounce : d[key],
  }));

const emailSentByDay = computed(() => toChartData('sent'));
const emailDeliveredByDay = computed(() => toChartData('delivered'));
const emailBouncedByDay = computed(() => toChartData('bounced'));

const loadData = async () => {
  if (!client.value) return;

  loading.value = true;
  error.value = null;

  try {
    const data = await client.value.getOverview();
    overviewData.value = data;
  } catch (err: any) {
    error.value = parseErrorMessage(err);
    console.error('Error loading overview data:', err);
  } finally {
    loading.value = false;
  }

  // Email stats load independently — a reporting outage shouldn't break the
  // rest of the overview.
  try {
    const stats = await client.value.getEmailStats(30);
    emailStats.value = stats;
    emailStatsConfigured.value = stats !== null;
  } catch (err) {
    console.error('Error loading email stats:', err);
    emailStats.value = null;
  }

  // Client versions load independently too, same reasoning.
  try {
    const { versions } = await client.value.getClientVersions();
    clientVersions.value = versions;
  } catch (err) {
    console.error('Error loading client version data:', err);
    clientVersions.value = [];
  }
};

onMounted(() => {
  loadData();
});
</script>

<style scoped>
.overview-dashboard {
  width: 100%;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loading-state p {
  margin: 0;
  color: #6b7280;
  font-style: italic;
}

.error-state p {
  margin: 0 0 1rem 0;
  color: #dc2626;
}

.retry-btn {
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.retry-btn:hover {
  background: #2563eb;
}

.overview-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.charts-section {
  width: 100%;
}

.section-heading {
  margin: 0 0 1rem 0;
  color: #374151;
  font-size: 1.125rem;
  font-weight: 600;
}

.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 1.5rem;
  width: 100%;
}

.activity-summary {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
}

.top-army-lists {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
}

.top-army-lists h3 {
  margin: 0 0 1rem 0;
  color: #1f2937;
  font-size: 1.125rem;
  font-weight: 600;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.empty-state {
  color: #6b7280;
  font-style: italic;
}

.top-army-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.top-army-list li {
  display: grid;
  grid-template-columns: 2rem 1fr auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
}

.rank {
  color: #6b7280;
  font-weight: 600;
}

.faction {
  color: #1f2937;
  font-weight: 500;
}

.count {
  color: #0f766e;
  font-weight: 700;
}

.count-with-delta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.activity-summary h3 {
  margin: 0 0 1rem 0;
  color: #1f2937;
  font-size: 1.125rem;
  font-weight: 600;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
  gap: 1rem;
}

.summary-label {
  color: #374151;
  font-weight: 500;
  font-size: 0.875rem;
}

.summary-value {
  color: #1f2937;
  font-weight: 600;
  font-size: 1rem;
}

.summary-value-group {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
}

.summary-delta {
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.delta-up {
  color: #16a34a;
}

.delta-down {
  color: #dc2626;
}

.delta-neutral {
  color: #6b7280;
}

/* Mobile responsive */
@media (max-width: 1024px) {
  .charts-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

@media (max-width: 768px) {
  .overview-content {
    gap: 1.5rem;
  }

  .charts-section {
    overflow: hidden; /* Prevent horizontal overflow */
  }

  .charts-grid {
    gap: 1rem;
    margin: 0 -0.5rem; /* Negative margin to use full width */
  }

  .activity-summary {
    padding: 1rem;
  }

  .top-army-lists {
    padding: 1rem;
  }

  .summary-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .summary-item {
    padding: 0.5rem;
    gap: 0.75rem;
  }

  .summary-label {
    font-size: 0.8rem;
  }

  .summary-value {
    font-size: 0.9rem;
  }

  .summary-value-group {
    align-items: flex-start;
  }
}
</style>
